import React, { useState, useEffect, useCallback, useRef } from 'react';
import './App.css';

import Topbar                  from './components/Topbar';
import InstructionsPanel       from './components/InstructionsPanel';
import EditorPanel             from './components/EditorPanel';
import TerminalPanel           from './components/TerminalPanel';
import RequirementsCard        from './components/RequirementsCard';
import MasteryReport           from './components/MasteryReport';
import CourseNavigationDrawer  from './components/CourseNavigationDrawer';
import CompetencyDashboard           from './pages/CompetencyDashboard';
import LMCSimulator                  from './pages/LMCSimulator';
import PacketSwitchingSimulator      from './pages/PacketSwitchingSimulator';
import TowersOfHanoiSimulator        from './pages/TowersOfHanoiSimulator';
import FractalDrawingSimulator        from './pages/FractalDrawingSimulator';
import DiffusionDenoisingSimulator     from './pages/DiffusionDenoisingSimulator';
import GANLoopSimulator                 from './pages/GANLoopSimulator';
import ConditionalGenerationSimulator   from './pages/ConditionalGenerationSimulator';
import VAELatentSpaceSimulator          from './pages/VAELatentSpaceSimulator';
import FlowReversibilitySimulator       from './pages/FlowReversibilitySimulator';
import LogicGateAdventure             from './pages/LogicGateAdventure';
import Help                          from './pages/Help';
import TeacherDashboard              from './pages/TeacherDashboard';
import AdminDashboard                from './pages/AdminDashboard';
import ThemeAHub                     from './pages/ThemeAHub';
import Bits                          from './pages/Bits';
import TrackOnboardingModal     from './components/TrackOnboardingModal';
import Auth                    from './components/Auth';
import AuthFlows, { AUTH_FLOW_PATHS } from './components/AuthFlows';
import MobileView              from './components/MobileView';
import { ALL_TOPICS, isPaper2Topic, isPaper2Accessible } from './curriculumConfig';
import { authHeaders }          from './authToken';

const API_URL = process.env.REACT_APP_API_URL || '';

// ── Helpers ──────────────────────────────────────────────────────────────────

function getStudentId() {
  let sid = localStorage.getItem('cs_student_id');
  if (!sid) {
    sid = (crypto.randomUUID && crypto.randomUUID()) ||
          'student_' + Math.random().toString(36).slice(2, 11);
    localStorage.setItem('cs_student_id', sid);
  }
  return sid;
}

// ── App ───────────────────────────────────────────────────────────────────────

export default function App() {
  // ── Mobile detection (< 768px = phone) ----------------------------------
  // Use min(width, height) so phones stay in mobile view when rotated to landscape.
  // Phone short-side ≈ 360–430px; tablets ≥ 600px — threshold of 550 cleanly separates them.
  const [isMobile, setIsMobile] = useState(
    () => Math.min(window.innerWidth, window.innerHeight) < 550
  );

  useEffect(() => {
    const handler = () => setIsMobile(Math.min(window.innerWidth, window.innerHeight) < 550);
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, []);

  // ── Theme ----------------------------------------------------------------
  const [theme, setTheme] = useState(
    () => localStorage.getItem('ib_theme') || 'light'
  );

  useEffect(() => {
    document.documentElement.setAttribute('data-theme', theme);
    localStorage.setItem('ib_theme', theme);
  }, [theme]);

  const toggleTheme = useCallback(() => {
    setTheme(t => t === 'light' ? 'dark' : 'light');
  }, []);

  // ── Accent colour theme ---------------------------------------------------
  const [accent, setAccent] = useState(
    () => localStorage.getItem('ib_accent') || 'violet'
  );

  useEffect(() => {
    document.documentElement.setAttribute('data-accent', accent);
    localStorage.setItem('ib_accent', accent);
  }, [accent]);

  // ── Page routing (simple state-based, no React Router) ------------------
  const [page, setPage] = useState(() => {
    const path = window.location.pathname.replace(/\/+$/, '') || '/';
    if (path === '/cpus')  return 'cpus';
    if (path === '/packs') return 'packs';
    if (path === '/hanoi') return 'hanoi';
    if (path === '/fractals') return 'fractals';
    if (path === '/diffusion') return 'diffusion';
    if (path === '/gan') return 'gan';
    if (path === '/conditional') return 'conditional';
    if (path === '/latent') return 'latent';
    if (path === '/reversible') return 'flow';
    if (path === '/caverns') return 'caverns';
    if (path === '/theme-a') return 'theme_a';
    if (path === '/teacher') return 'teacher';
    if (path === '/admin')   return 'admin';
    if (path === '/help')  return 'help';
    if (path === '/bits' || path.startsWith('/bits/')) return 'bits';
    return 'dashboard';
  });

  // A /bits/<slug> deep link opens that bit directly. Frozen at mount so App
  // re-renders (theme toggles, etc.) never re-trigger the Bits load effect.
  const [bitsInitialId] = useState(() => {
    const p = window.location.pathname.replace(/\/+$/, '');
    return p.startsWith('/bits/') ? p.slice('/bits/'.length) : null;
  });

  // Sync URL path with page state so /cpus and /packs are shareable links
  useEffect(() => {
    if (page === 'cpus') {
      if (window.location.pathname !== '/cpus') window.history.pushState(null, '', '/cpus');
    } else if (page === 'packs') {
      if (window.location.pathname !== '/packs') window.history.pushState(null, '', '/packs');
    } else if (page === 'hanoi') {
      if (window.location.pathname !== '/hanoi') window.history.pushState(null, '', '/hanoi');
    } else if (page === 'fractals') {
      if (window.location.pathname !== '/fractals') window.history.pushState(null, '', '/fractals');
    } else if (page === 'diffusion') {
      if (window.location.pathname !== '/diffusion') window.history.pushState(null, '', '/diffusion');
    } else if (page === 'gan') {
      if (window.location.pathname !== '/gan') window.history.pushState(null, '', '/gan');
    } else if (page === 'conditional') {
      if (window.location.pathname !== '/conditional') window.history.pushState(null, '', '/conditional');
    } else if (page === 'latent') {
      if (window.location.pathname !== '/latent') window.history.pushState(null, '', '/latent');
    } else if (page === 'flow') {
      if (window.location.pathname !== '/reversible') window.history.pushState(null, '', '/reversible');
    } else if (page === 'caverns') {
      if (window.location.pathname !== '/caverns') window.history.pushState(null, '', '/caverns');
    } else if (page === 'theme_a') {
      if (window.location.pathname !== '/theme-a') window.history.pushState(null, '', '/theme-a');
    } else if (page === 'teacher') {
      if (window.location.pathname !== '/teacher') window.history.pushState(null, '', '/teacher');
    } else if (page === 'admin') {
      if (window.location.pathname !== '/admin') window.history.pushState(null, '', '/admin');
    } else if (page === 'help') {
      if (window.location.pathname !== '/help') window.history.pushState(null, '', '/help');
    } else if (page === 'bits') {
      // Bits manages its own /bits/<slug> sub-URLs; only claim the base path.
      if (!window.location.pathname.startsWith('/bits')) window.history.pushState(null, '', '/bits');
    } else if (['/cpus', '/packs', '/hanoi', '/fractals', '/diffusion', '/gan', '/conditional', '/latent', '/reversible', '/caverns', '/theme-a', '/teacher', '/admin', '/help'].includes(window.location.pathname) || window.location.pathname.startsWith('/bits')) {
      window.history.replaceState(null, '', '/');
    }
  }, [page]);

  // Handle browser back/forward through /cpus and /packs
  useEffect(() => {
    const handler = () => {
      const path = window.location.pathname.replace(/\/+$/, '') || '/';
      if (path === '/cpus')  setPage('cpus');
      else if (path === '/packs') setPage('packs');
      else if (path === '/hanoi') setPage('hanoi');
      else if (path === '/fractals') setPage('fractals');
      else if (path === '/diffusion') setPage('diffusion');
      else if (path === '/gan') setPage('gan');
      else if (path === '/conditional') setPage('conditional');
      else if (path === '/latent') setPage('latent');
      else if (path === '/reversible') setPage('flow');
      else if (path === '/caverns') setPage('caverns');
      else if (path === '/theme-a') setPage('theme_a');
      else if (path === '/teacher') setPage('teacher');
      else if (path === '/admin') setPage('admin');
      else if (path === '/help') setPage('help');
      else if (path === '/bits' || path.startsWith('/bits/')) setPage('bits');
      else setPage(prev => (prev === 'cpus' || prev === 'packs' || prev === 'hanoi' || prev === 'fractals' || prev === 'diffusion' || prev === 'gan' || prev === 'conditional' || prev === 'latent' || prev === 'flow' || prev === 'caverns' || prev === 'theme_a' || prev === 'teacher' || prev === 'bits' || prev === 'help') ? 'dashboard' : prev);
    };
    window.addEventListener('popstate', handler);
    return () => window.removeEventListener('popstate', handler);
  }, []);

  // ── Active language track (Python / Java) --------------------------------
  const [activeLang, setActiveLang] = useState('python');

  // ── Active IB level track (SL / HL) --------------------------------------
  const [activeLevel, setActiveLevel] = useState('hl');

  // ── Curriculum drawer ---------------------------------------------------
  const [drawerOpen, setDrawerOpen] = useState(false);

  // ── Dashboard tab control (lets the drawer deep-link into a tab) --------
  // Default 'locker' so the dashboard still lands on The Locker after login.
  const [dashboardTab, setDashboardTab] = useState('locker');
  // A {scope, nonce} request that opens the Theory Review tab on a subtopic.
  const [theoryScopeReq, setTheoryScopeReq] = useState(null);

  // Nonce that tells CompetencyDashboard to drop any in-progress Flashcards/Quiz/
  // SQL view it shows internally (page-level routing can't see that state).
  const [homeNonce, setHomeNonce] = useState(0);

  const openDashboardTab = useCallback((tab) => {
    setDashboardTab(tab);
    setPage('dashboard');
    // Also drop out of any in-progress study view, so a nav click from inside a
    // quiz actually navigates instead of appearing to do nothing.
    setHomeNonce(n => n + 1);
  }, []);

  // "Home" — the XP pill and the brand logo both call this. It always lands on
  // the dashboard hub (never toggles away to an exercise); the homeNonce above
  // tells CompetencyDashboard to drop out of any in-progress study view.
  const goHome = useCallback(() => {
    setPage('dashboard');
    setHomeNonce(n => n + 1);
  }, []);

  const openTheoryScope = useCallback((scope) => {
    setDashboardTab('theory');
    setTheoryScopeReq({ scope, nonce: Date.now() });
    setPage('dashboard');
    // Exit any in-progress study view so the requested subtopic is shown.
    setHomeNonce(n => n + 1);
  }, []);

  // ── Active unit (supports switching between available units) ------------
  const [activeUnitId, setActiveUnitId] = useState('b2_1');

  // Derive the active topic from the config (used by drawer highlight + topbar)
  const activeTopic = ALL_TOPICS.find(t => t.unitId === activeUnitId) ?? null;

  // ── Auth gate -----------------------------------------------------------
  const [isAuthenticated, setIsAuthenticated] = useState(!!localStorage.getItem('student_id'));
  const [studentId, setStudentId] = useState(localStorage.getItem('student_id') || getStudentId());
  const [displayName, setDisplayName] = useState(localStorage.getItem('display_name') || '');
  const [sessionExpired, setSessionExpired] = useState(false);

  // Sliding session + graceful expiry. Wrap fetch once: store the refreshed token
  // the server ships on X-Refresh-Token (so ongoing activity keeps the session
  // alive), and on a 401 to an authenticated /api call, log out cleanly to the
  // login screen with a notice instead of leaving a half-broken state.
  useEffect(() => {
    const orig = window.fetch;
    window.fetch = async (...args) => {
      const res = await orig(...args);
      try {
        const url = typeof args[0] === 'string' ? args[0] : (args[0] && args[0].url) || '';
        if (url.includes('/api/')) {
          const fresh = res.headers.get('X-Refresh-Token');
          if (fresh) localStorage.setItem('auth_token', fresh);
          if (res.status === 401 && !url.includes('/api/login') && localStorage.getItem('auth_token')) {
            localStorage.removeItem('auth_token');
            localStorage.removeItem('student_id');
            setSessionExpired(true);
            setIsAuthenticated(false);
          }
        }
      } catch (e) { /* non-fatal */ }
      return res;
    };
    return () => { window.fetch = orig; };
  }, []);

  // Theme A access. Open to everyone during the friends-and-family test, so the
  // gate defaults open unless the server explicitly says '0'. The flag plumbing
  // (server theme_a_access, refreshed by fetchPlayerData) stays in place for
  // when we re-gate to pilot users.
  const [themeAAccess, setThemeAAccess] = useState(localStorage.getItem('theme_a_access') !== '0');

  // ── Cosmetics state (persisted via localStorage + backend) ---------------
  const [equippedAvatarId, setEquippedAvatarId] = useState(localStorage.getItem('active_avatar') || 'avatar_default');
  const [equippedRingId,   setEquippedRingId]   = useState(localStorage.getItem('active_ring')   || 'ring_none');
  const [equippedPetId,    setEquippedPetId]     = useState(localStorage.getItem('active_pet')    || 'pet_none');

  // ── Core state ----------------------------------------------------------
  const [unitTitle,  setUnitTitle]  = useState('');
  const [exercise,   setExercise]   = useState(null);
  const [code,       setCode]       = useState('');

  const [feedback,         setFeedback]         = useState(null);
  const [testResults,      setTestResults]       = useState({});
  const [nextExercise,     setNextExercise]      = useState(null);
  const [nextReason,       setNextReason]        = useState('');
  const [unitComplete,     setUnitComplete]      = useState(false);
  const [attempts,         setAttempts]          = useState(0);
  const [consecutiveFails, setConsecutiveFails]  = useState(0);
  const [progress,         setProgress]          = useState(null);
  const [unitExercises,    setUnitExercises]     = useState([]);

  // ── Panel collapse state ------------------------------------------------
  const [leftCollapsed,  setLeftCollapsed]  = useState(false);
  const [rightCollapsed, setRightCollapsed] = useState(false);

  // ── Session history for backward navigation -----------------------------
  // Each entry: { exercise, seqIndex } where seqIndex = unit-sequence position
  const [sessionHistory, setSessionHistory] = useState([]);
  const [historyPos,     setHistoryPos]     = useState(0);

  // ── Mastery Engine state ------------------------------------------------
  const [manualRunCount, setManualRunCount] = useState(0);
  const [masteryReport,  setMasteryReport]  = useState(null);
  const [masteryOverall, setMasteryOverall] = useState(null); // { percent, topic }

  // Live player data fetched from the DB (XP, unit completion counts)
  const [playerData, setPlayerData] = useState(null);

  const [loading,         setLoading]         = useState(false);
  const [isRunning,       setIsRunning]       = useState(false);
  const [error,           setError]           = useState('');
  // Milestone overlay: null | 'sl_core_complete' | 'unit_complete'
  const [activeMilestone, setActiveMilestone] = useState(null);

  // Refs
  const terminalRef    = useRef(null);
  const runAndCheckRef = useRef(null);
  const starterCodeRef = useRef('');
  // Tracks whether activeLang has changed at least once after the initial mount.
  // Prevents the activeLang effect from firing a duplicate startUnit on first render
  // (the mount effect already calls startUnit once).
  const langChangedRef = useRef(false);
  // Applies the student's stored course track (if any) to activeLang/activeLevel
  // exactly once, the first time fetchPlayerData resolves it.
  const trackAppliedRef = useRef(false);
  // Sends a role-based first-login redirect (teacher → /teacher) exactly once,
  // and only when the user landed on the bare root path.
  const roleRedirectDoneRef = useRef(false);

  // ── Student's own course track (Python/Java + SL/HL) ---------------------
  // null = not yet known; true/false once the first mastery fetch resolves it.
  const [trackSet, setTrackSet] = useState(null);

  const updateTrack = useCallback(async (language, level) => {
    setActiveLang(language);
    setActiveLevel(level);
    setTrackSet(true);
    trackAppliedRef.current = true;
    try {
      await fetch(`${API_URL}/api/account/update-track`, {
        method:  'POST',
        headers: authHeaders({ 'Content-Type': 'application/json' }),
        body:    JSON.stringify({ language, level }),
      });
    } catch (_) { /* best-effort; local state already reflects the choice */ }
  }, []);

  const language  = exercise?.language      || 'python';
  const isExplore = exercise?.type === 'explore';

  // Terminal colour tokens
  const termBg   = theme === 'dark' ? '#100c08' : '#211c16';
  const termText = theme === 'dark' ? '#f0e8db' : '#f3eadb';

  // ── Fetch player data (XP, unit completions) from the DB ─────────────────
  // Pass the active language track so the response is language-specific.
  // global_lifetime_xp (max across all languages) is used for cosmetics.
  const fetchPlayerData = useCallback(async (lang) => {
    const language = lang ?? activeLang;
    try {
      const r = await fetch(`${API_URL}/api/mastery/${studentId}?language=${language}&level=${activeLevel}`, {
        headers: authHeaders(),
      });
      if (!r.ok) return;
      const d = await r.json();
      if (d.theme_a_access !== undefined) {
        setThemeAAccess(!!d.theme_a_access);
        localStorage.setItem('theme_a_access', d.theme_a_access ? '1' : '0');
      }
      if (d.overall) setMasteryOverall(d.overall);
      if (d.track_set !== undefined) {
        setTrackSet(!!d.track_set);
        // Apply the student's stored track once, the first time it's known —
        // corrects activeLang/activeLevel away from their hardcoded defaults
        // without fighting a user-initiated change made via the chip.
        if (d.track_set && !trackAppliedRef.current) {
          trackAppliedRef.current = true;
          if (d.stored_language && d.stored_language !== activeLang)  setActiveLang(d.stored_language);
          if (d.stored_level    && d.stored_level    !== activeLevel) setActiveLevel(d.stored_level);
        }
      }
      if (d.lifetime_xp !== undefined || d.units) {
        setPlayerData({
          lifetimeXP:       d.lifetime_xp          ?? 0,
          globalXP:         d.global_lifetime_xp   ?? d.lifetime_xp ?? 0,
          // Combined pool (both themes) — drives the shared avatar level.
          combinedXP:       d.combined_lifetime_xp ?? d.global_lifetime_xp ?? d.lifetime_xp ?? 0,
          units:            d.units                ?? {},
          is_teacher:       d.is_teacher          ?? false,
          is_admin:         d.is_admin            ?? false,
          // Freemium entitlement — drives the premium lock UI. Without these the
          // locks fall back to the free-tier defaults, which would wrongly lock a
          // paying account out of its own content.
          entitled:             d.entitled              ?? false,
          subscription_status:  d.subscription_status   ?? 'free',
          subscription_plan:    d.subscription_plan     ?? null,
          teacher_verified:     d.teacher_verified      ?? false,
          free_coding_prefixes: d.free_coding_prefixes  ?? ['b1'],
          free_theory_prefixes: d.free_theory_prefixes  ?? ['A1'],
          free_languages:       d.free_languages        ?? ['python'],
          polyglot:         d.polyglot           ?? false,
          language:         d.language           ?? language,
          stats:            d.stats              ?? { cleanRuns: 0, totalExercises: 0, currentStreak: 0, maxStreak: 0 },
          earnedBadgeIds:   d.earnedBadgeIds     ?? [],
        });
      }
    } catch (_) {}
  }, [studentId, activeLang, activeLevel]);

  // ── API -----------------------------------------------------------------

  const startUnit = useCallback(async (unitId) => {
    setError('');
    try {
      const r = await fetch(`${API_URL}/api/start`, {
        method:  'POST',
        headers: authHeaders({ 'Content-Type': 'application/json' }),
        // Send the active language track so the backend can resolve the correct
        // language variant (e.g. 'b3_1' + 'java' → 'b3_1_java'). Identity comes
        // from the bearer token, so student_id is no longer sent in the body.
        body:    JSON.stringify({ unit_id: unitId, language: activeLang, level: activeLevel }),
      });
      if (!r.ok) throw new Error(`Server returned ${r.status}`);
      const data = await r.json();
      setUnitTitle(data.unit.title);
      loadExerciseData(data.exercise);
      setProgress(data.progress);
      if (data.unit_exercises) setUnitExercises(data.unit_exercises);
      const seqIdx = data.progress?.completed_count ?? 0;
      setSessionHistory([{ exercise: data.exercise, seqIndex: seqIdx }]);
      setHistoryPos(0);
      if (data.resumed) setNextReason('Resuming where you left off.');
    } catch (err) {
      setError(`Could not connect to server: ${err.message}`);
    }
    fetchPlayerData();
  }, [studentId, activeLang, fetchPlayerData]); // eslint-disable-line react-hooks/exhaustive-deps

  // Start initial unit once authenticated.
  // Using isAuthenticated as the dep means:
  //   - returning users (already authenticated on mount) → fires immediately
  //   - new users (not yet authenticated on mount) → fires after login completes
  // The plain [] mount approach fired before login was complete, leaving Python
  // data empty until the user switched language tabs.
  useEffect(() => {
    if (isAuthenticated) startUnit(activeUnitId);
  }, [isAuthenticated]); // eslint-disable-line react-hooks/exhaustive-deps

  // When the active language track changes:
  //   1. Refresh player data for the new track.
  //   2. Hot-swap the active workspace by re-running startUnit with the new language
  //      so the editor loads the correct language variant immediately.
  //
  // langChangedRef guards against a duplicate startUnit call on the initial
  // render — the mount effect (below) already handles that first load.
  useEffect(() => {
    fetchPlayerData(activeLang);
    if (langChangedRef.current) {
      startUnit(activeUnitId);
    }
    langChangedRef.current = true;
  }, [activeLang, activeLevel]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Unit switching -------------------------------------------------------

  const handleSelectTopic = useCallback((topic) => {
    if (!topic?.unitId) return;
    // Paper 2 practice is gated behind completing every core Theme B unit in the
    // active language track (teachers are exempt). Refuse to open it until that
    // gate is cleared, so the drawer and any deep links honour the same rule as
    // the dashboard matrix.
    if (isPaper2Topic(topic) && !isPaper2Accessible(playerData, activeLang, activeLevel)) {
      return;
    }
    setActiveUnitId(topic.unitId);
    setDrawerOpen(false);
    setPage('exercise');
    startUnit(topic.unitId);
  }, [startUnit, playerData, activeLang, activeLevel]);

  const loadExerciseData = (ex) => {
    starterCodeRef.current = ex.starter_code || '';
    if (ex?.title) {
      localStorage.setItem('last_active_exercise', JSON.stringify({ name: ex.title, id: ex.id }));
    }
    setExercise(ex);
    setCode(ex.starter_code || '');
    setFeedback(null);
    setTestResults({});
    setAttempts(0);
    setConsecutiveFails(0);
    setNextExercise(null);
    setNextReason('');
    setUnitComplete(false);
    setManualRunCount(0);
    setMasteryReport(null);
    setActiveMilestone(null);
    terminalRef.current?.clear();
  };

  const submitCode = useCallback(async (codeSnapshot, exerciseSnapshot, runCount) => {
    if (!exerciseSnapshot || loading) return;
    setLoading(true);
    setError('');
    try {
      const r = await fetch(`${API_URL}/api/submit`, {
        method:  'POST',
        headers: authHeaders({ 'Content-Type': 'application/json' }),
        body:    JSON.stringify({
          code:             codeSnapshot,
          exercise_id:      exerciseSnapshot.id,
          unit_id:          activeUnitId,
          manual_run_count: runCount ?? manualRunCount,
          // Tell the backend which language track is active so it resolves the
          // correct unit variant (e.g. 'b3_1' + 'java' → 'b3_1_java') for
          // exercise lookup, code execution, and progress recording.
          language:         activeLang,
          // IB level track — used for SL off-ramp interception on the backend.
          level:            activeLevel,
        }),
      });
      if (!r.ok) throw new Error(`Server returned ${r.status}`);
      const result = await r.json();

      setFeedback(result.feedback);
      setTestResults(result.execution?.test_results || {});
      setAttempts(result.attempts);
      setConsecutiveFails(result.consecutive_fails);
      setNextExercise(result.next_exercise);
      setNextReason(result.next_reason || '');
      setUnitComplete(result.unit_complete);
      setProgress(result.progress);
      if (result.unit_exercises) setUnitExercises(result.unit_exercises);

      // Mastery — refresh player XP whenever the exercise is passed
      if (result.mastery_report) {
        setMasteryReport(result.mastery_report);
      }
      if (result.passed) {
        fetchPlayerData();
      }

      // Milestone — surface the appropriate celebration overlay
      if (result.milestone) {
        setActiveMilestone(result.milestone);
      }
    } catch (err) {
      setError(`Submit failed: ${err.message}`);
    } finally {
      setLoading(false);
    }
  }, [loading, activeLang, manualRunCount, activeUnitId, fetchPlayerData, activeLevel]);

  // ── Actions -------------------------------------------------------------

  const handleRun = useCallback(() => {
    if (!exercise || isRunning || loading) return;
    setRightCollapsed(false); // auto-expand terminal on run

    const newCount = manualRunCount + 1;
    setManualRunCount(newCount);

    // Run always executes code in the terminal only — never auto-submits.
    // For explore exercises the student must click Submit explicitly, which
    // prevents the task being marked complete before they make the instructed change.
    terminalRef.current?.startRun(code, language);
  }, [code, exercise, isRunning, language, loading, manualRunCount]);

  const handleRunAndCheck = useCallback(() => {
    if (!exercise || isRunning || loading) return;
    setRightCollapsed(false); // auto-expand terminal on run
    const codeSnap     = code;
    const exerciseSnap = exercise;
    const runCount     = manualRunCount + 1;
    setManualRunCount(runCount);
    terminalRef.current?.startRun(codeSnap, language, () => {
      submitCode(codeSnap, exerciseSnap, runCount);
    });
  }, [code, exercise, language, isRunning, loading, manualRunCount, submitCode]);

  // Keep ref in sync for Monaco keybinding
  useEffect(() => { runAndCheckRef.current = handleRunAndCheck; }, [handleRunAndCheck]);

  const handleEditorMount = useCallback((editor, monaco) => {
    editor.addCommand(
      monaco.KeyMod.CtrlCmd | monaco.KeyCode.Enter,
      () => runAndCheckRef.current?.()
    );
  }, []);

  const handleReset = useCallback(() => {
    if (!exercise) return;
    if (window.confirm('Reset to starter code?')) {
      setCode(starterCodeRef.current);
    }
  }, [exercise]);

  // Teachers (allowlist role) may roam the whole unit without completing
  // anything. XP/badges are still earned only through real submissions.
  const isTeacher = !!playerData?.is_teacher;
  const isAdmin = !!playerData?.is_admin;

  // Guard the teacher/admin pages: bounce anyone who reaches them without the
  // role (e.g. by typing the URL) back to the dashboard once their profile loads.
  useEffect(() => {
    if (page === 'teacher' && isAuthenticated && playerData && !isTeacher) {
      setPage('dashboard');
    }
    if (page === 'admin' && isAuthenticated && playerData && !isAdmin) {
      setPage('dashboard');
    }
  }, [page, isAuthenticated, playerData, isTeacher, isAdmin]);

  // Role-based default landing page: a teacher's home is their class dashboard,
  // not the student Mastery Hub. Fires once, only for a bare "/" landing — never
  // overrides an explicit deep link (e.g. /cpus) or a page the user navigated to.
  useEffect(() => {
    if (roleRedirectDoneRef.current || !isAuthenticated || !playerData) return;
    roleRedirectDoneRef.current = true;
    const path = window.location.pathname.replace(/\/+$/, '') || '/';
    if (isTeacher && path === '/') {
      setPage('teacher');
    }
  }, [isAuthenticated, playerData, isTeacher]);

  const advance = useCallback(() => {
    if (!nextExercise) return;
    const seqIdx  = progress?.completed_count ?? 0;
    const newEntry = { exercise: nextExercise, seqIndex: seqIdx };
    setSessionHistory(prev => [...prev.slice(0, historyPos + 1), newEntry]);
    setHistoryPos(historyPos + 1);
    loadExerciseData(nextExercise);
  }, [nextExercise, progress, historyPos]); // eslint-disable-line react-hooks/exhaustive-deps

  const handlePrevious = useCallback(async () => {
    if (historyPos > 0) {
      const newPos = historyPos - 1;
      setHistoryPos(newPos);
      loadExerciseData(sessionHistory[newPos].exercise);
    } else {
      const idx = unitExercises.findIndex(ex => ex.id === exercise?.id);
      if (idx > 0) {
        const prevId = unitExercises[idx - 1].id;
        try {
          const r = await fetch(`${API_URL}/api/exercise/${prevId}?unit_id=${activeUnitId}&language=${activeLang}`, { headers: authHeaders() });
          if (r.ok) {
            const prevEx = await r.json();
            setSessionHistory(prev => [{ exercise: prevEx, seqIndex: idx - 1 }, ...prev]);
            loadExerciseData(prevEx);
          }
        } catch (e) {
          console.error("Failed to fetch previous exercise", e);
        }
      }
    }
  }, [historyPos, sessionHistory, unitExercises, exercise?.id, activeUnitId, activeLang]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleNext = useCallback(async () => {
    if (historyPos < sessionHistory.length - 1) {
      const newPos = historyPos + 1;
      setHistoryPos(newPos);
      loadExerciseData(sessionHistory[newPos].exercise);
      return;
    }
    // Teachers step to the next exercise in the unit by index — no pass
    // required — mirroring how Previous already walks back through the unit.
    if (isTeacher) {
      const idx = unitExercises.findIndex(ex => ex.id === exercise?.id);
      if (idx >= 0 && idx < unitExercises.length - 1) {
        const nextId = unitExercises[idx + 1].id;
        try {
          const r = await fetch(`${API_URL}/api/exercise/${nextId}?unit_id=${activeUnitId}&language=${activeLang}`, { headers: authHeaders() });
          if (r.ok) {
            const nextEx = await r.json();
            setSessionHistory(prev => [...prev, { exercise: nextEx, seqIndex: idx + 1 }]);
            setHistoryPos(prev => prev + 1);
            loadExerciseData(nextEx);
          }
        } catch (e) {
          console.error("Failed to fetch next exercise", e);
        }
        return;
      }
    }
    advance();
  }, [historyPos, sessionHistory, advance, isTeacher, unitExercises, exercise?.id, activeUnitId, activeLang]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Derived -------------------------------------------------------------

  const allPassed = (
    (Object.keys(testResults).length > 0 && Object.values(testResults).every(r => r.passed)) ||
    (isExplore && feedback?.passed === true)
  );

  const isRetry = nextExercise && exercise && nextExercise.id === exercise.id;

  const willOfferScaffold =
    consecutiveFails >= 2 && !feedback?.passed && exercise?.scaffold;

  const canAdvanceToNew = !!nextExercise && !isRetry && (allPassed || willOfferScaffold);

  const currentIndexInUnit = unitExercises.findIndex(ex => ex.id === exercise?.id);
  // Teachers can always step forward while there's a later exercise in the unit.
  const teacherCanStepForward =
    isTeacher && currentIndexInUnit >= 0 && currentIndexInUnit < unitExercises.length - 1;
  const canAdvance = historyPos < sessionHistory.length - 1 || canAdvanceToNew || teacherCanStepForward;
  const currentSeqIndex = currentIndexInUnit >= 0 ? currentIndexInUnit : (sessionHistory[historyPos]?.seqIndex ?? 0);
  const totalTasks      = progress?.total_count ?? 8;

  // ── Cosmetics equip handler ─────────────────────────────────────────────
  const handleEquipCosmetic = useCallback(async (type, value) => {
    // The server authorises the equip against the unlock level (defence in
    // depth — the Locker UI already hides locked items). Only persist locally
    // when the server accepts, so a rejected equip never sticks in the UI.
    try {
      const res = await fetch(`${API_URL}/api/user/update-cosmetics`, {
        method:  'POST',
        headers: authHeaders({ 'Content-Type': 'application/json' }),
        body:    JSON.stringify({ type, value }),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        if (body?.reason) alert(body.reason);
        return;
      }
    } catch (_) {
      return; // network error — don't optimistically equip
    }
    const key = type === 'avatar' ? 'active_avatar' : type === 'ring' ? 'active_ring' : 'active_pet';
    localStorage.setItem(key, value);
    if (type === 'avatar') setEquippedAvatarId(value);
    else if (type === 'ring') setEquippedRingId(value);
    else if (type === 'pet') setEquippedPetId(value);
  }, []);

  // ── Cosmetics merged into playerData for downstream components ──────────
  const playerDataWithCosmetics = {
    ...(playerData || {}),
    equippedAvatarId,
    equippedRingId,
    equippedPetId,
  };

  // ── Shared topbar props -------------------------------------------------

  const topbarProps = {
    unitTitle,
    exercise,
    theme,
    onToggleTheme:      toggleTheme,
    accent,
    onAccentChange:     setAccent,
    progress,
    canAdvance,
    onNext:             handleNext,
    onPrevious:         handlePrevious,
    currentTaskIndex:   currentSeqIndex,
    totalTasks,
    masteryOverall,
    playerData: playerDataWithCosmetics,
    onOpenDashboard:    goHome,
    onOpenDrawer:       () => setDrawerOpen(true),
    onLogout:           () => {
      localStorage.removeItem('auth_token');
      localStorage.removeItem('student_id');
      localStorage.removeItem('account_type');
      localStorage.removeItem('display_name');
      localStorage.removeItem('active_avatar');
      localStorage.removeItem('active_ring');
      localStorage.removeItem('active_pet');
      localStorage.removeItem('theme_a_access');
      setThemeAAccess(false);
      setIsAuthenticated(false);
      setStudentId('');
      setDisplayName('');
      setEquippedAvatarId('avatar_default');
      setEquippedRingId('ring_none');
      setEquippedPetId('pet_none');
    },
    displayName,
    onDisplayNameChange: (name) => { setDisplayName(name); localStorage.setItem('display_name', name); },
    unitExercises,
    currentExerciseId:  exercise?.id,
  };

  // ── Auth gate (after all hooks) -----------------------------------------

  // Invite / password-reset flows are reached directly by URL from an email and
  // must render regardless of auth state (before the login gate below).
  if (AUTH_FLOW_PATHS.includes(window.location.pathname.replace(/\/+$/, ''))) {
    return <AuthFlows />;
  }

  // Public simulators — accessible before and after login
  if (page === 'cpus') {
    return <LMCSimulator onBack={() => setPage('dashboard')} />;
  }

  if (page === 'packs') {
    return <PacketSwitchingSimulator onBack={() => setPage('dashboard')} />;
  }

  if (page === 'hanoi') {
    return <TowersOfHanoiSimulator onBack={() => setPage('dashboard')} />;
  }

  if (page === 'fractals') {
    return <FractalDrawingSimulator onBack={() => setPage('dashboard')} />;
  }

  if (page === 'diffusion') {
    return <DiffusionDenoisingSimulator onBack={() => setPage('dashboard')} />;
  }

  if (page === 'gan') {
    return <GANLoopSimulator onBack={() => setPage('dashboard')} />;
  }

  if (page === 'conditional') {
    return <ConditionalGenerationSimulator onBack={() => setPage('dashboard')} />;
  }

  if (page === 'latent') {
    return <VAELatentSpaceSimulator onBack={() => setPage('dashboard')} />;
  }

  if (page === 'flow') {
    return <FlowReversibilitySimulator onBack={() => setPage('dashboard')} />;
  }

  if (page === 'caverns') {
    return <LogicGateAdventure onBack={() => setPage('dashboard')} />;
  }

  // Help / tutorial — one platform-aware page for both desktop and mobile
  if (page === 'help') {
    return <Help onBack={() => setPage('dashboard')} isTeacher={isTeacher} />;
  }

  if (!isAuthenticated) {
    return (
      <Auth
        notice={sessionExpired ? 'Your session expired. Please log in again.' : ''}
        onNavigateToCPUS={() => setPage('cpus')}
        onNavigateToPacks={() => setPage('packs')}
        onNavigateToHanoi={() => setPage('hanoi')}
        onNavigateToFractals={() => setPage('fractals')}
        onNavigateToDiffusion={() => setPage('diffusion')}
        onNavigateToGan={() => setPage('gan')}
        onNavigateToConditional={() => setPage('conditional')}
        onNavigateToLatent={() => setPage('latent')}
        onNavigateToFlow={() => setPage('flow')}
        onNavigateToCaverns={() => setPage('caverns')}
        onAuthSuccess={(uid, _accountType, name) => {
        setStudentId(uid);
        setDisplayName(name || '');
        setSessionExpired(false);
        setIsAuthenticated(true);
        setEquippedAvatarId(localStorage.getItem('active_avatar') || 'avatar_default');
        setEquippedRingId(localStorage.getItem('active_ring')     || 'ring_none');
        setEquippedPetId(localStorage.getItem('active_pet')       || 'pet_none');
      }} />
    );
  }

  // ── brookebits — authenticated code scratchpad + saved bits -------------
  // Rendered before the mobile gate so the editor is reachable on phones too
  // (the layout stacks editor over terminal on narrow screens).

  if (page === 'bits') {
    return (
      <div className="workshop">
        <Topbar {...topbarProps} isDashboard={true} canAdvance={false} />
        <div className="workshop-body dashboard-body">
          <Bits initialBitId={bitsInitialId} theme={theme} isAdmin={isAdmin} entitled={!!playerData?.entitled} />
        </div>
        <CourseNavigationDrawer
          open={drawerOpen}
          onClose={() => setDrawerOpen(false)}
          activeTopicId={activeTopic?.id}
          onSelectTopic={handleSelectTopic}
          isTopicLocked={(t) => isPaper2Topic(t) && !isPaper2Accessible(playerData, activeLang, activeLevel)}
          onNavigateToCodingLabs={() => openDashboardTab('coding')}
          onNavigateToTheoryReview={() => openDashboardTab('theory')}
          onNavigateToLocker={() => openDashboardTab('locker')}
          onSelectThemeAScope={openTheoryScope}
          onNavigateToCPUS={() => setPage('cpus')}
          onNavigateToPacks={() => setPage('packs')}
          onNavigateToHanoi={() => setPage('hanoi')}
          onNavigateToFractals={() => setPage('fractals')}
          onNavigateToDiffusion={() => setPage('diffusion')}
          onNavigateToGan={() => setPage('gan')}
          onNavigateToConditional={() => setPage('conditional')}
          onNavigateToLatent={() => setPage('latent')}
          onNavigateToFlow={() => setPage('flow')}
          onNavigateToCaverns={() => setPage('caverns')}
          onNavigateToHelp={() => setPage('help')}
          onNavigateToBits={() => setPage('bits')}
          onNavigateToTeacher={() => setPage('teacher')}
          isTeacher={isTeacher}
          onNavigateToAdmin={() => setPage('admin')}
          isAdmin={isAdmin}
          showThemeA={themeAAccess}
        />
      </div>
    );
  }

  // ── Theme A (theory) study hub — authenticated, full-page ---------------

  if (page === 'theme_a') {
    return (
      <ThemeAHub
        activeLevel={activeLevel}
        onLevelChange={setActiveLevel}
        onBack={() => setPage('dashboard')}
      />
    );
  }

  // ── Teacher dashboard — separate screen, teachers only -------------------

  if (page === 'teacher' && isTeacher) {
    return <TeacherDashboard onBack={() => setPage('dashboard')} playerData={playerData} />;
  }

  if (page === 'admin' && isAdmin) {
    return <AdminDashboard onBack={() => setPage('dashboard')} />;
  }

  // ── Mobile view (phone-only stats dashboard) ----------------------------

  if (isMobile) {
    return (
      <MobileView
        playerData={playerDataWithCosmetics}
        displayName={displayName}
        studentId={studentId}
        themeAAccess={themeAAccess}
        theme={theme}
        onToggleTheme={toggleTheme}
        onLogout={topbarProps.onLogout}
        onEquipCosmetic={handleEquipCosmetic}
        activeLang={activeLang}
        activeLevel={activeLevel}
        onLangChange={setActiveLang}
        onLevelChange={setActiveLevel}
        onNavigateToCPUS={() => setPage('cpus')}
        onNavigateToPacks={() => setPage('packs')}
        onNavigateToHanoi={() => setPage('hanoi')}
        onNavigateToFractals={() => setPage('fractals')}
        onNavigateToDiffusion={() => setPage('diffusion')}
        onNavigateToGan={() => setPage('gan')}
        onNavigateToConditional={() => setPage('conditional')}
        onNavigateToLatent={() => setPage('latent')}
        onNavigateToFlow={() => setPage('flow')}
        onNavigateToCaverns={() => setPage('caverns')}
        onNavigateToHelp={() => setPage('help')}
        onNavigateToBits={() => setPage('bits')}
        onNavigateToTeacher={() => setPage('teacher')}
        isTeacher={isTeacher}
        onNavigateToAdmin={() => setPage('admin')}
        isAdmin={isAdmin}
      />
    );
  }

  // ── Dashboard page -------------------------------------------------------

  if (page === 'dashboard') {
    return (
      <div className="workshop">
        <Topbar {...topbarProps} isDashboard={true} canAdvance={false} />
        {!isTeacher && trackSet === false && (
          <TrackOnboardingModal language={activeLang} level={activeLevel} onSave={updateTrack} />
        )}
        <div className="workshop-body dashboard-body">
          <CompetencyDashboard
            studentId={studentId}
            playerData={playerDataWithCosmetics}
            displayName={displayName}
            onEquipCosmetic={handleEquipCosmetic}
            activeLang={activeLang}
            activeLevel={activeLevel}
            onTrackChange={updateTrack}
            onBack={() => setPage('exercise')}
            onSelectTopic={handleSelectTopic}
            controlledTab={dashboardTab}
            onTabChange={setDashboardTab}
            theoryScopeReq={theoryScopeReq}
            themeAAccess={themeAAccess}
            goHomeSignal={homeNonce}
            onOpenBits={() => setPage('bits')}
          />
        </div>
        <CourseNavigationDrawer
          open={drawerOpen}
          onClose={() => setDrawerOpen(false)}
          activeTopicId={activeTopic?.id}
          onSelectTopic={handleSelectTopic}
          isTopicLocked={(t) => isPaper2Topic(t) && !isPaper2Accessible(playerData, activeLang, activeLevel)}
          onNavigateToCodingLabs={() => openDashboardTab('coding')}
          onNavigateToTheoryReview={() => openDashboardTab('theory')}
          onNavigateToLocker={() => openDashboardTab('locker')}
          onSelectThemeAScope={openTheoryScope}
          onNavigateToCPUS={() => setPage('cpus')}
          onNavigateToPacks={() => setPage('packs')}
          onNavigateToHanoi={() => setPage('hanoi')}
          onNavigateToFractals={() => setPage('fractals')}
        onNavigateToDiffusion={() => setPage('diffusion')}
        onNavigateToGan={() => setPage('gan')}
        onNavigateToConditional={() => setPage('conditional')}
        onNavigateToLatent={() => setPage('latent')}
        onNavigateToFlow={() => setPage('flow')}
          onNavigateToCaverns={() => setPage('caverns')}
          onNavigateToHelp={() => setPage('help')}
          onNavigateToBits={() => setPage('bits')}
          onNavigateToTeacher={() => setPage('teacher')}
          isTeacher={isTeacher}
          onNavigateToAdmin={() => setPage('admin')}
          isAdmin={isAdmin}
          showThemeA={themeAAccess}
        />
      </div>
    );
  }

  // ── Exercise page --------------------------------------------------------

  return (
    <div className="workshop">

      {/* ── Topbar ──────────────────────────────────────────────────── */}
      <Topbar {...topbarProps} />

      {!isTeacher && trackSet === false && (
        <TrackOnboardingModal language={activeLang} level={activeLevel} onSave={updateTrack} />
      )}

      {/* ── Error bar ───────────────────────────────────────────────── */}
      {error && <div className="error-bar">{error}</div>}

      {/* ── Three-column grid ───────────────────────────────────────── */}
      <main
        className="workshop-body"
        style={{
          gridTemplateColumns: `${leftCollapsed ? '36px' : '350px'} 1fr ${rightCollapsed ? '36px' : '480px'}`,
          transition: 'grid-template-columns 0.22s ease',
        }}
      >

        {/* Col 1: Instructions + Requirements */}
        <div className={`left-col${leftCollapsed ? ' col-collapsed' : ''}`} style={{ position: 'relative' }}>
          {/* Collapse toggle — vertically centred on the right border */}
          <button
            className="panel-toggle panel-toggle-left"
            onClick={() => setLeftCollapsed(c => !c)}
            title={leftCollapsed ? 'Expand instructions' : 'Collapse instructions'}
            aria-label={leftCollapsed ? 'Expand instructions panel' : 'Collapse instructions panel'}
          >
            {leftCollapsed ? '›' : '‹'}
          </button>

          {!leftCollapsed && (
            <>
              <InstructionsPanel
                exercise={exercise}
                feedback={feedback}
                consecutiveFails={consecutiveFails}
                unitComplete={unitComplete}
                unitTitle={unitTitle}
                allPassed={allPassed}
                nextExercise={nextExercise}
                isRetry={isRetry}
                nextReason={nextReason}
                willOfferScaffold={willOfferScaffold}
              />
              {!isExplore && (exercise?.test_cases?.length > 0) && (
                <RequirementsCard exercise={exercise} testResults={testResults} />
              )}
            </>
          )}
        </div>

        {/* Col 2: Editor */}
        <EditorPanel
          exercise={exercise}
          code={code}
          onChange={setCode}
          language={language}
          theme={theme}
          onRun={handleRun}
          onRunAndCheck={handleRunAndCheck}
          onReset={handleReset}
          onEditorMount={handleEditorMount}
          attempts={attempts}
          testResults={testResults}
          isRunning={isRunning}
          loading={loading}
          isExplore={isExplore}
        />

        {/* Col 3: Terminal + MasteryReport */}
        <div className={`right-col${rightCollapsed ? ' col-collapsed' : ''}`} style={{ position: 'relative' }}>
          {/* Collapse toggle — vertically centred on the left border */}
          <button
            className="panel-toggle panel-toggle-right"
            onClick={() => setRightCollapsed(c => !c)}
            title={rightCollapsed ? 'Expand terminal' : 'Collapse terminal'}
            aria-label={rightCollapsed ? 'Expand terminal panel' : 'Collapse terminal panel'}
          >
            {rightCollapsed ? '‹' : '›'}
          </button>

          {!rightCollapsed && (
            <>
              <TerminalPanel
                ref={terminalRef}
                onRunStateChange={setIsRunning}
                termBg={termBg}
                termText={termText}
              />
              {allPassed && masteryReport && (
                <MasteryReport
                  data={masteryReport}
                  onContinue={canAdvanceToNew ? handleNext : undefined}
                />
              )}
            </>
          )}
        </div>

      </main>

      {/* ── Curriculum Drawer ────────────────────────────────────────── */}
      <CourseNavigationDrawer
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        activeTopicId={activeTopic?.id}
        onSelectTopic={handleSelectTopic}
        isTopicLocked={(t) => isPaper2Topic(t) && !isPaper2Accessible(playerData, activeLang, activeLevel)}
        onNavigateToCodingLabs={() => openDashboardTab('coding')}
        onNavigateToTheoryReview={() => openDashboardTab('theory')}
        onNavigateToLocker={() => openDashboardTab('locker')}
        onSelectThemeAScope={openTheoryScope}
        onNavigateToCPUS={() => setPage('cpus')}
        onNavigateToPacks={() => setPage('packs')}
        onNavigateToHanoi={() => setPage('hanoi')}
        onNavigateToFractals={() => setPage('fractals')}
        onNavigateToDiffusion={() => setPage('diffusion')}
        onNavigateToGan={() => setPage('gan')}
        onNavigateToConditional={() => setPage('conditional')}
        onNavigateToLatent={() => setPage('latent')}
        onNavigateToFlow={() => setPage('flow')}
        onNavigateToCaverns={() => setPage('caverns')}
        onNavigateToHelp={() => setPage('help')}
        onNavigateToBits={() => setPage('bits')}
        onNavigateToTeacher={() => setPage('teacher')}
        isTeacher={isTeacher}
        onNavigateToAdmin={() => setPage('admin')}
        isAdmin={isAdmin}
        showThemeA={themeAAccess}
      />

      {/* ── Milestone overlay ─────────────────────────────────────────── */}
      {activeMilestone && (
        <div style={{
          position: 'fixed',
          inset: 0,
          zIndex: 9999,
          backdropFilter: 'blur(8px)',
          background: 'rgba(20, 12, 5, 0.45)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}>
          <div className="mr-shimmer-card" style={{
            background: 'var(--surface)',
            border: '1px solid var(--border)',
            borderRadius: '16px',
            padding: '24px',
            maxWidth: '480px',
            textAlign: 'center',
            boxShadow: 'var(--card-shadow)',
          }}>
            {activeMilestone === 'sl_core_complete' ? (
              <>
                <h2 style={{ fontFamily: 'Fraunces, serif', fontSize: '24px', marginBottom: '12px', margin: 0 }}>🎉 SL Core Complete! (100%)</h2>
                <p style={{ color: 'var(--text-muted)', lineHeight: '1.6', marginBottom: '20px', marginTop: '12px' }}>
                  You have completed all Standard Level requirements for this unit. You can return to your profile, or stretch yourself with the Higher Level extension tasks as an optional bonus.
                </p>
                <div style={{ display: 'flex', gap: '10px', justifyContent: 'center' }}>
                  <button className="action-btn action-run-secondary" onClick={() => { setActiveMilestone(null); setPage('dashboard'); }}>Return to Dashboard</button>
                  <button className="action-btn action-run-primary" onClick={() => setActiveMilestone(null)}>Try HL Stretch Goal</button>
                </div>
              </>
            ) : (
              <>
                <h2 style={{ fontFamily: 'Fraunces, serif', fontSize: '24px', marginBottom: '12px', margin: 0 }}>🏆 Unit Completed!</h2>
                <p style={{ color: 'var(--text-muted)', lineHeight: '1.6', marginBottom: '20px', marginTop: '12px' }}>
                  Spectacular work! You have fully cleared every exercise inside this unit module.
                </p>
                <button className="action-btn action-run-primary" onClick={() => { setActiveMilestone(null); setPage('dashboard'); }}>Return to Dashboard</button>
              </>
            )}
          </div>
        </div>
      )}

    </div>
  );
}
